import * as THREE from 'three'
import { moveObject3DChildToIndex } from '../../../utils/scene-object/addObject3DToParent'

/**
 * 原始显示 Mesh 的实例投影脱离控制器。
 *
 * 业务对象仍保留 geometry、material、metadata 与局部变换，但正式 GPU 投影激活后不再作为
 * 主场景树节点参与遍历和渲染。控制器集中保存原父级、兄弟顺序和业务显隐，并为脱离对象
 * 提供等价世界矩阵，保证拖动、旋转与 topology 投影继续使用同一份权威业务对象。
 */
export class ImportedModelSourceMeshVisibilityController {
  /** source Mesh 到业务显隐和抑制 owner 的状态表。 */
  private readonly stateByMesh = new Map<THREE.Mesh, PuppyCAD.ImportedModelSourceMeshVisibilityState>()

  /** 原业务父对象到已脱离直属 source Mesh 的反向索引。 */
  private readonly detachedSourceMeshesByOriginalParent = new Map<THREE.Object3D, Set<THREE.Mesh>>()

  /** 最近一次脱离序号；恢复时按逆序处理，保证同父级多个 Mesh 的原始顺序。 */
  private detachmentSequence = 0

  /** 递归解析脱离父链时复用的矩阵，避免变换热路径创建临时对象。 */
  private readonly parentWorldMatrix = new THREE.Matrix4()

  /**
   * 由指定实例组接管一个 source Mesh 的正式显示并将其移出主场景树。
   *
   * @param sourceMesh 待抑制普通 Mesh。
   * @param ownerId 实例组稳定运行时键。
   */
  public suppress(sourceMesh: THREE.Mesh, ownerId: string): void {
    const existingState = this.stateByMesh.get(sourceMesh)
    if (existingState) {
      existingState.ownerIds.add(ownerId)
      return
    }
    const originalParent = sourceMesh.parent
    if (!originalParent) {
      throw new Error(`实例投影 source Mesh 缺少原始父级：${sourceMesh.uuid}`)
    }
    const originalSiblingIndex = originalParent.children.indexOf(sourceMesh)
    if (originalSiblingIndex < 0) {
      throw new Error(`实例投影 source Mesh 不在原始父级 children 中：${sourceMesh.uuid}`)
    }
    const state: PuppyCAD.ImportedModelSourceMeshVisibilityState = {
      businessVisible: sourceMesh.visible,
      ownerIds: new Set<string>([ownerId]),
      originalParent,
      originalSiblingIndex,
      detachmentSequence: this.detachmentSequence,
      originalMatrixWorldAutoUpdate: sourceMesh.matrixWorldAutoUpdate,
    }
    this.detachmentSequence += 1
    this.stateByMesh.set(sourceMesh, state)
    const detachedSourceMeshes = this.detachedSourceMeshesByOriginalParent.get(originalParent)
      ?? new Set<THREE.Mesh>()
    detachedSourceMeshes.add(sourceMesh)
    this.detachedSourceMeshesByOriginalParent.set(originalParent, detachedSourceMeshes)
    originalParent.remove(sourceMesh)
    /** 空间查询仍读取业务 Mesh 的 matrixWorld，但脱离期间只能由本控制器显式维护。 */
    sourceMesh.matrixWorldAutoUpdate = false
  }

  /**
   * 释放指定实例组拥有的全部 source Mesh 抑制。
   *
   * @param ownerId 实例组稳定运行时键。
   */
  public releaseOwner(ownerId: string): void {
    const releasableStates = [...this.stateByMesh.entries()]
      .filter(([, state]) => state.ownerIds.has(ownerId))
      .sort((left, right) => right[1].detachmentSequence - left[1].detachmentSequence)
    releasableStates.forEach(([sourceMesh, state]) => {
      state.ownerIds.delete(ownerId)
      if (state.ownerIds.size > 0) {
        return
      }
      this.restoreSourceMesh(sourceMesh, state)
      this.stateByMesh.delete(sourceMesh)
      this.removeDetachedSourceMeshFromOriginalParentIndex(sourceMesh, state.originalParent)
    })
  }

  /**
   * 复制 source Mesh 当前有效世界矩阵。
   *
   * @param sourceMesh 已由实例投影接管或仍挂载场景的源 Mesh。
   * @param targetMatrix 接收世界矩阵的复用对象。
   * @returns 对象存在有效场景父链或脱离记录时返回 true。
   */
  public copyEffectiveWorldMatrix(
    sourceMesh: THREE.Mesh,
    targetMatrix: THREE.Matrix4,
  ): boolean {
    return this.copyObjectWorldMatrix(sourceMesh, targetMatrix)
  }

  /**
   * 读取 source Mesh 脱离前的权威父对象。
   *
   * @param sourceMesh 已由实例投影接管的业务 Mesh。
   * @returns 原父对象；当前 Mesh 未被接管时返回 null。
   */
  public resolveOriginalParent(sourceMesh: THREE.Mesh): THREE.Object3D | null {
    return this.stateByMesh.get(sourceMesh)?.originalParent ?? null
  }

  /**
   * 读取对象在实例投影接管前的业务逻辑子节点。
   *
   * 脱离记录的 siblingIndex 按各自脱离时刻保存，因此必须按脱离逆序重新插入，才能同时
   * 恢复多个 source Mesh 的原始顺序。本方法只创建只读快照，不把 Mesh 临时挂回渲染树。
   *
   * @param parentObject 待读取的业务父对象。
   * @returns 合并普通子节点和已脱离 source Mesh 后的逻辑子节点。
   */
  public listLogicalChildren(parentObject: THREE.Object3D): readonly THREE.Object3D[] {
    return this.mergeLogicalChildren(parentObject, parentObject.children)
  }

  /**
   * 将当前控制器拥有的脱离 source Mesh 合并到已有逻辑子节点快照。
   *
   * Viewer 可能同时存在装配体加载投影和阵列独立投影；各领域控制器必须在同一份快照上
   * 依次恢复自己的直属子节点，禁止后一个控制器用物理 children 覆盖前一个控制器的结果。
   *
   * @param parentObject 待读取的业务父对象。
   * @param currentChildren 已由其它实例投影控制器恢复的逻辑子节点。
   * @returns 合并当前控制器脱离节点后的只读顺序快照。
   */
  public mergeLogicalChildren(
    parentObject: THREE.Object3D,
    currentChildren: readonly THREE.Object3D[],
  ): readonly THREE.Object3D[] {
    const logicalChildren = [...currentChildren]
    const detachedChildren: Array<readonly [
      THREE.Mesh,
      PuppyCAD.ImportedModelSourceMeshVisibilityState,
    ]> = []
    this.detachedSourceMeshesByOriginalParent.get(parentObject)?.forEach((sourceMesh) => {
      const state = this.stateByMesh.get(sourceMesh)
      if (state) {
        detachedChildren.push([sourceMesh, state])
      }
    })
    detachedChildren.sort((left, right) => (
      right[1].detachmentSequence - left[1].detachmentSequence
    ))
    detachedChildren.forEach(([sourceMesh, state]) => {
      const currentIndex = logicalChildren.indexOf(sourceMesh)
      if (currentIndex >= 0) {
        logicalChildren.splice(currentIndex, 1)
      }
      const insertionIndex = Math.min(
        Math.max(0, state.originalSiblingIndex),
        logicalChildren.length,
      )
      logicalChildren.splice(insertionIndex, 0, sourceMesh)
    })
    return Object.freeze(logicalChildren)
  }

  /**
   * 为同步只读导出临时恢复指定场景根内的全部业务 source Mesh。
   *
   * @param sceneRoot 当前离线快照遍历根。
   * @returns 本轮实际恢复、需要在 finally 中重新脱离的 Mesh 集合。
   */
  public restoreSourceMeshesForRead(sceneRoot: THREE.Object3D): readonly THREE.Mesh[] {
    const states = [...this.stateByMesh.entries()]
      .filter(([, state]) => this.isInsideSceneRoot(state.originalParent, sceneRoot))
      .sort((left, right) => right[1].detachmentSequence - left[1].detachmentSequence)
    states.forEach(([sourceMesh, state]) => {
      this.restoreSourceMesh(sourceMesh, state)
    })
    return Object.freeze(states.map(([sourceMesh]) => sourceMesh))
  }

  /**
   * 结束同步只读导出后，把临时恢复的业务 Mesh 再次移出主场景树。
   *
   * @param sourceMeshes `restoreSourceMeshesForRead` 返回的精确对象集合。
   */
  public detachSourceMeshesAfterRead(sourceMeshes: readonly THREE.Mesh[]): void {
    sourceMeshes.forEach((sourceMesh) => {
      if (this.stateByMesh.has(sourceMesh)) {
        sourceMesh.removeFromParent()
        sourceMesh.matrixWorldAutoUpdate = false
      }
    })
  }

  /** 恢复全部 source Mesh 的业务显隐并清空运行时状态。 */
  public dispose(): void {
    ;[...this.stateByMesh.entries()]
      .sort((left, right) => right[1].detachmentSequence - left[1].detachmentSequence)
      .forEach(([sourceMesh, state]) => {
        this.restoreSourceMesh(sourceMesh, state)
      })
    this.stateByMesh.clear()
    this.detachedSourceMeshesByOriginalParent.clear()
  }

  /**
   * 解析普通场景对象或已脱离 source Mesh 的有效世界矩阵。
   *
   * @param object 当前对象。
   * @param targetMatrix 接收世界矩阵的复用对象。
   * @returns 对象仍可解析时返回 true。
   */
  private copyObjectWorldMatrix(
    object: THREE.Object3D,
    targetMatrix: THREE.Matrix4,
  ): boolean {
    const detachedState = object instanceof THREE.Mesh
      ? this.stateByMesh.get(object)
      : undefined
    if (!detachedState) {
      object.updateWorldMatrix(true, false)
      targetMatrix.copy(object.matrixWorld)
      return true
    }

    if (object.matrixAutoUpdate) {
      object.updateMatrix()
    }
    if (!this.copyObjectWorldMatrix(detachedState.originalParent, this.parentWorldMatrix)) {
      return false
    }
    targetMatrix.multiplyMatrices(this.parentWorldMatrix, object.matrix)
    object.matrixWorld.copy(targetMatrix)
    object.matrixWorldNeedsUpdate = false
    return true
  }

  /**
   * 把 source Mesh 恢复到原父级和原兄弟下标。
   *
   * @param sourceMesh 待恢复业务 Mesh。
   * @param state 脱离前保存的稳定状态。
   */
  private restoreSourceMesh(
    sourceMesh: THREE.Mesh,
    state: PuppyCAD.ImportedModelSourceMeshVisibilityState,
  ): void {
    sourceMesh.removeFromParent()
    sourceMesh.visible = state.businessVisible
    sourceMesh.matrixWorldAutoUpdate = state.originalMatrixWorldAutoUpdate
    state.originalParent.add(sourceMesh)
    const insertionIndex = Math.min(
      Math.max(0, state.originalSiblingIndex),
      Math.max(0, state.originalParent.children.length - 1),
    )
    moveObject3DChildToIndex(state.originalParent, sourceMesh, insertionIndex)
  }

  /**
   * 从原父级反向索引中移除已经恢复的 source Mesh。
   *
   * @param sourceMesh 已恢复的 source Mesh。
   * @param originalParent source Mesh 脱离前的父对象。
   */
  private removeDetachedSourceMeshFromOriginalParentIndex(
    sourceMesh: THREE.Mesh,
    originalParent: THREE.Object3D,
  ): void {
    const detachedSourceMeshes = this.detachedSourceMeshesByOriginalParent.get(originalParent)
    if (!detachedSourceMeshes) {
      return
    }
    detachedSourceMeshes.delete(sourceMesh)
    if (detachedSourceMeshes.size === 0) {
      this.detachedSourceMeshesByOriginalParent.delete(originalParent)
    }
  }

  /**
   * 判断原父对象是否属于指定场景根；父对象本身为脱离 Mesh 时沿保存父链继续判断。
   *
   * @param object 起始父对象。
   * @param sceneRoot 目标场景根。
   * @returns 当前业务对象属于该导出根时返回 true。
   */
  private isInsideSceneRoot(object: THREE.Object3D, sceneRoot: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      if (current === sceneRoot) {
        return true
      }
      const detachedState = current instanceof THREE.Mesh
        ? this.stateByMesh.get(current)
        : undefined
      current = detachedState?.originalParent ?? current.parent
    }
    return false
  }
}
